import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { VERDICT_STYLE, speedStyle } from './branchOverviewStyles';
import SOURCE_LABELS from '../../config/sourceLabels';
import { daysInStockSeverity } from '../../utils/dealershipSettings';
import { useDealershipSettings } from '../../contexts/settingsContext';
import {
    coverageTotals, findPhotoIssues, findUnlistedUnits, groupPhotoIssuesByBranch, groupUnlistedByBranch,
    unlistedForBranch,
} from '../../utils/listingCoverage';

export default function MFCoverageView({
    invUnits, matrixData, matrixLoading, matrixError, onRetry, onOpenUnit,
}) {
    const { aging } = useDealershipSettings();
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [photoSelectedBranch, setPhotoSelectedBranch] = useState(null);
    const [subView, setSubView] = useState('unlisted'); // 'unlisted' | 'photos'

    // Shared with the v2 tab (utils/listingCoverage).
    const unlistedUnits = useMemo(() => findUnlistedUnits(invUnits, matrixData?.rows), [invUnits, matrixData]);
    const photoRows = useMemo(() => findPhotoIssues(matrixData?.rows), [matrixData]);
    const photoBranchRows = useMemo(() => groupPhotoIssuesByBranch(photoRows), [photoRows]);

    const activePhotoBranch = photoSelectedBranch || photoBranchRows[0]?.branch;
    const drillPhotoUnits   = useMemo(() =>
        photoRows.filter(r => r.branch === activePhotoBranch),
        [photoRows, activePhotoBranch]
    );

    // Branch breakdown
    const branchRows = useMemo(() => groupUnlistedByBranch(unlistedUnits), [unlistedUnits]);

    const activeBranch = selectedBranch || branchRows[0]?.branch;

    // Selected branch units — sorted oldest first
    const drillUnits = useMemo(() => unlistedForBranch(unlistedUnits, activeBranch), [unlistedUnits, activeBranch]);

    // Totals
    const { value: totalValue, verySlow: verySlowCount } = coverageTotals(unlistedUnits);

    // A failed /pricing-matrix has nothing to compare against: say so rather
    // than reporting every unit as "Not listed" (P-BR-073).
    if (matrixError && !matrixLoading) {
        return (
            <div className="bo-error" role="alert">
                {matrixError}
                {onRetry && (
                    <button type="button" className="bco-list-action" onClick={onRetry} style={{ marginLeft: '0.75rem' }}>
                        Retry
                    </button>
                )}
            </div>
        );
    }

    if (matrixLoading || !matrixData || !invUnits.length) {
        return <div className="bo-loading"><div className="bo-spinner" />Loading coverage data...</div>;
    }

    return (
        <div className="bo-section">
            <div className="bo-section-header">
                <h2>{SOURCE_LABELS.listingCoverageGap}</h2>
                <span className="bo-meta">Listing quality signals across active inventory</span>
            </div>

            {/* Summary strip — always visible */}
            <div className="mfcg-summary">
                <div className="mfcg-stat" style={{ cursor: 'pointer' }} onClick={() => setSubView('unlisted')}>
                    <span className="mfcg-stat-num">{unlistedUnits.length}</span>
                    <span className="mfcg-stat-label">Not Listed</span>
                </div>
                <div className="mfcg-stat" style={{ cursor: 'pointer' }} onClick={() => setSubView('unlisted')}>
                    <span className="mfcg-stat-num">${(totalValue / 1_000_000).toFixed(1)}M</span>
                    <span className="mfcg-stat-label">Value Unlisted</span>
                </div>
                <div className={`mfcg-stat ${verySlowCount > 0 ? 'mfcg-stat-warn' : ''}`} style={{ cursor: 'pointer' }} onClick={() => setSubView('unlisted')}>
                    <span className="mfcg-stat-num" style={{ color: verySlowCount > 0 ? '#b91c1c' : undefined }}>{verySlowCount}</span>
                    <span className="mfcg-stat-label">Very Slow Unlisted</span>
                </div>
                <div className="mfcg-stat" style={{ cursor: 'pointer' }} onClick={() => setSubView('photos')}>
                    <span className="mfcg-stat-num" style={{ color: photoRows.length > 0 ? '#b45309' : undefined }}>{photoRows.length}</span>
                    <span className="mfcg-stat-label">Below-Avg Photos</span>
                </div>
            </div>

            {/* Sub-view tabs */}
            <div className="mfcg-sub-tabs">
                <button className={`mfcg-sub-tab ${subView === 'unlisted' ? 'mfcg-sub-active' : ''}`} onClick={() => setSubView('unlisted')}>
                    Not Listed
                </button>
                <button className={`mfcg-sub-tab ${subView === 'photos' ? 'mfcg-sub-active' : ''}`} onClick={() => setSubView('photos')}>
                    Photo Quality
                </button>
            </div>

            {/* ── Unlisted sub-view ── */}
            {subView === 'unlisted' && (
                <div className="bco-layout">
                    <div className="bco-list">
                        {branchRows.map(b => (
                            <button
                                key={b.branch}
                                className={`bco-list-item ${b.branch === activeBranch ? 'bco-list-item-active' : ''}`}
                                onClick={() => setSelectedBranch(b.branch)}
                            >
                                <div className="bco-list-row">
                                    <span className="bco-list-name">{b.branch}</span>
                                    <span className="bco-list-count">{b.count} unlisted</span>
                                </div>
                                {b.value > 0 && (
                                    <span className="bco-list-value">${(b.value / 1_000_000).toFixed(2)}M</span>
                                )}
                                <div className="bco-mini-bar">
                                    {b.fast     > 0 && <div className="bco-mini-seg" style={{ width: `${(b.fast/b.total)*100}%`,     background: '#15803d' }} />}
                                    {b.normal   > 0 && <div className="bco-mini-seg" style={{ width: `${(b.normal/b.total)*100}%`,   background: '#1d4ed8' }} />}
                                    {b.slow     > 0 && <div className="bco-mini-seg" style={{ width: `${(b.slow/b.total)*100}%`,     background: '#b45309' }} />}
                                    {b.verySlow > 0 && <div className="bco-mini-seg" style={{ width: `${(b.verySlow/b.total)*100}%`, background: '#b91c1c' }} />}
                                </div>
                            </button>
                        ))}
                    </div>
                    <div className="bco-detail">
                        <div className="bco-detail-header">
                            <h3 className="bco-detail-title">{activeBranch}</h3>
                            <span className="bco-detail-total">{drillUnits.length} unlisted</span>
                        </div>
                        {drillUnits.length === 0 ? (
                            <div className="bo-empty">No unlisted units for this branch.</div>
                        ) : (
                            <div className="mfcg-unit-list">
                                {drillUnits.map(u => {
                                    const val      = u.Best_Sell_Price || u.SALES_VALUE || 0;
                                    const days     = u.DaysInStock || 0;
                                    const severity = { crit: 'critical', warn: 'warn' }[daysInStockSeverity(days, aging)] || 'ok';
                                    const vStyle   = u.AnomalyVerdict ? VERDICT_STYLE[u.AnomalyVerdict] : null;
                                    const sStyle   = speedStyle(u.DaysToSellCategory);
                                    const isAnomaly = vStyle && !['Outperformer','High Interest'].includes(u.AnomalyVerdict);
                                    return (
                                        <div key={u.StockNumber} className={`mfcg-card mfcg-card-${severity}`}>
                                            <div className="mfcg-card-top">
                                                <div className="mfcg-card-title">
                                                    {[u.YEAR_MANUF, u.MAKE, u.MODEL].filter(Boolean).join(' ')}
                                                </div>
                                                <button
                                                    className="mfcg-stock-link"
                                                    title="Open unit detail"
                                                    onClick={() => onOpenUnit(u)}
                                                >
                                                    #{u.StockNumber}
                                                </button>
                                            </div>
                                            <div className="mfcg-card-row">
                                                <span className="mfcg-card-cat">{u.CATEGORY || '—'}</span>
                                                <span className={`mfcg-card-days mfcg-days-${severity}`}>{days}d in stock</span>
                                                {val > 0 && <span className="mfcg-card-val">${Math.round(val).toLocaleString('en-US')}</span>}
                                            </div>
                                            {(sStyle || isAnomaly) && (
                                                <div className="mfcg-card-signals">
                                                    {sStyle && <span className="ut-signal-badge" style={{ background: sStyle.bg, color: sStyle.color }}>{u.DaysToSellCategory}</span>}
                                                    {isAnomaly && <span className="ut-signal-badge" style={{ background: vStyle.bg, color: vStyle.color }}>{u.AnomalyVerdict}</span>}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── Photo Quality sub-view ── */}
            {subView === 'photos' && (
                <div className="bco-layout">
                    <div className="bco-list">
                        {photoBranchRows.map(b => (
                            <button
                                key={b.branch}
                                className={`bco-list-item ${b.branch === activePhotoBranch ? 'bco-list-item-active' : ''}`}
                                onClick={() => setPhotoSelectedBranch(b.branch)}
                            >
                                <div className="bco-list-row">
                                    <span className="bco-list-name">{b.branch}</span>
                                    <span className="bco-list-count">{b.count} flagged</span>
                                </div>
                                {b.noPhotos > 0 && (
                                    <span className="mfcg-no-photo-badge">{b.noPhotos} no photos</span>
                                )}
                            </button>
                        ))}
                    </div>
                    <div className="bco-detail">
                        <div className="bco-detail-header">
                            <h3 className="bco-detail-title">{activePhotoBranch}</h3>
                            <span className="bco-detail-total">{drillPhotoUnits.length} below avg</span>
                        </div>
                        {drillPhotoUnits.length === 0 ? (
                            <div className="bo-empty">No photo issues for this branch.</div>
                        ) : (
                            <div className="mfcg-unit-list">
                                {drillPhotoUnits.map((r, i) => {
                                    const severity = r.photo_count === 0 ? 'critical' : 'warn';
                                    const photoDiff = r.avg_photos != null ? Math.round(r.avg_photos - r.photo_count) : null;
                                    return (
                                        <div key={r.stock_number || i} className={`mfcg-card mfcg-card-${severity}`}>
                                            <div className="mfcg-card-top">
                                                <div className="mfcg-card-title">
                                                    {[r.year, r.make, r.model].filter(Boolean).join(' ')}
                                                </div>
                                                {r.stock_number && (
                                                    <button
                                                        className="mfcg-stock-link"
                                                        title="Open unit detail"
                                                        onClick={() => {
                                                            const unit = invUnits.find(u => String(u.StockNumber) === String(r.stock_number));
                                                            if (unit) onOpenUnit(unit);
                                                        }}
                                                    >
                                                        #{r.stock_number}
                                                    </button>
                                                )}
                                            </div>
                                            <div className="mfcg-card-row">
                                                <span className="mfcg-card-cat">{r.is_new === true ? 'New' : 'Used'}</span>
                                                <span className={`mfcg-photo-count mfcg-photo-${severity === 'critical' ? 'none' : 'low'}`}>
                                                    {r.photo_count === 0 ? 'No photos' : `${r.photo_count} photos`}
                                                </span>
                                                {r.avg_photos != null && (
                                                    <span className="mfcg-photo-avg">
                                                        avg {r.avg_photos}{photoDiff > 0 ? ` · needs +${photoDiff}` : ''}
                                                    </span>
                                                )}
                                                <span className="mfcg-card-val">${Math.round(r.price).toLocaleString('en-US')}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}


MFCoverageView.propTypes = {
    invUnits: PropTypes.arrayOf(PropTypes.object),
    matrixData: PropTypes.object,
    matrixLoading: PropTypes.bool,
    matrixError: PropTypes.string,
    onRetry: PropTypes.func,
    onOpenUnit: PropTypes.func.isRequired,
};

MFCoverageView.defaultProps = {
    invUnits: [],
    matrixData: null,
    matrixLoading: false,
    matrixError: null,
    onRetry: null,
};
